import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Swal from 'sweetalert2';
import { useModal } from '../../hooks/useModal';
import { quotationService, referenceService, companyService, productService, authService, tenderService } from '../../services/api';
import { cleanText } from '../../utils/textHelpers';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const QuotationManagement = ({ user }) => {
  const canManage = ['Administrator', 'Manager'].includes(user?.role);
  const [quotations, setQuotations] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [products, setProducts] = useState([]);
  const [tenders, setTenders] = useState([]); // New tenders state
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(true);

  const calculateValidityDate = (startDate, days) => {
    if (!startDate || !days) return '';
    const date = new Date(startDate);
    date.setDate(date.getDate() + parseInt(days));
    return date.toISOString().split('T')[0];
  };

  const stages = [
    { id: 1, name: 'Draft', color: 'secondary' },
    { id: 2, name: 'Submitted', color: 'info' },
    { id: 3, name: 'Awarded', color: 'success' },
    { id: 4, name: 'Rejected', color: 'danger' },
    { id: 5, name: 'Cancelled', color: 'dark' },
    { id: 6, name: 'Revision', color: 'warning' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [qs, orgs, prods, conts, tds, companyRes] = await Promise.all([
        quotationService.getAll(),
        referenceService.getOrgs(),
        productService.getAll(),
        referenceService.getContacts(),
        tenderService.getAll(), // Fetch tenders
        companyService.getProfile()
      ]);
      setQuotations(qs.data || []);
      setOrganizations((orgs.data || []).sort((a, b) => a.name.localeCompare(b.name)));
      setProducts(prods.data || []);
      setContacts(conts.data || []);
      setTenders(tds.data || []); // Set tenders
      setCompany(companyRes.data || {});
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };



  const formatDisplayValue = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  };

  const loadQuotations = async () => {
    const response = await quotationService.getAll();
    setQuotations(response.data);
  };

  const [showModal, setShowModal] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [searchTerm, setSearchTerm] = useState(''); // Added search state

  useModal(showModal || showStageModal || showViewModal, () => {
    setShowModal(false);
    setShowStageModal(false);
    setShowViewModal(false);
  });

  const initialFormState = {
    title: '',
    client: '', // Kept for legacy state compatibility if needed, but UI uses organization
    organization: '', // New field mapping
    tenderId: '', // New Tender Link
    designation: '',
    subject: '',
    subject: '',
    submissionDate: new Date().toISOString().split('T')[0],
    validityDate: '',
    validityDays: 30, // Default 30 days
    gstRate: 18.00,
    installationCharges: 0,
    status: 'Draft',
    currentStage: 1,
    stageName: 'Draft',
    distributorLogo: 'default', // 'default', 'none', 'logo1', 'logo2', etc. or index
    termsAndConditions: `1. Warranty: 1-Year
2. Delivery: Within 1-3 weeks upon Confirmation of order
3. Payment: 100% Advance - Negotiable
4. Validity: 30 days
5. Since there is rapid updation in technology, the quoted item/s are based on current stock with OEM. In case the offered item is discontinued or Not available at the time of PO, alternate / equivalent item will be supplied.
6. Taxes: All TAXES are NOT deductible from our bill. All items will be imported through proper channel. All applicable taxes will be paid at the time of import, Since FBR has prohibited deduction of double tax at the time of payment. Therefore deductioin of any tax from our bill will not be accepetable. Incase tax is deducted from our bill, same amount will be invoiced to end customer for payment.
7. World Wide Vision will be not responsible for any failure to perform its obligations under this Quotation, if it is prevented or delayed in performing those obligations by an event of force majeure.
8. The scope of delivery of World Wide Vision is limited to above quoted items only. Any additional item/material will be subject to written prior approval of additional price approval separately.
9. This quotation & its terms & conditions will be the part of contract.`
  };

  const [formData, setFormData] = useState(initialFormState);
  const [items, setItems] = useState([]);

  const handleAddItem = () => {
    setItems([...items, { _tempId: Date.now(), productName: '', description: '', modelNumber: '', quantity: 1, unitPrice: 0, totalPrice: 0 }]);
  };

  const handleRemoveItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];

    if (typeof field === 'object' && field !== null) {
      // Handle bulk update
      newItems[index] = { ...newItems[index], ...field };
    } else {
      // Handle single field update
      newItems[index][field] = value;
    }

    // Re-calculate total price with adjustments
    const qty = parseFloat(newItems[index].quantity) || 0;
    const unitPrice = parseFloat(newItems[index].unitPrice) || 0;
    const adjPercent = parseFloat(newItems[index].adjustmentPercentage) || 0;
    const adjType = newItems[index].adjustmentType || 'None';

    let adjustedUnitPrice = unitPrice;
    if (adjType === 'Add') {
      adjustedUnitPrice = unitPrice * (1 + adjPercent / 100);
    } else if (adjType === 'Subtract') {
      adjustedUnitPrice = unitPrice * (1 - adjPercent / 100);
    }

    newItems[index].totalPrice = qty * adjustedUnitPrice;

    setItems(newItems);
  };

  const calculateTotals = () => {
    const subTotal = items.reduce((sum, item) => sum + (parseFloat(item.totalPrice) || 0), 0);
    const gstAmount = (subTotal * (parseFloat(formData.gstRate) || 0)) / 100;
    const installCharges = parseFloat(formData.installationCharges) || 0;
    const grandTotal = subTotal + gstAmount + installCharges;
    return { subTotal, gstAmount, grandTotal };
  };

  const handleDeleteQuotation = async (id) => {
    if (await Swal.fire({ title: 'Delete?', icon: 'warning', showCancelButton: true }).then(r => r.isConfirmed)) {
      try {
        await quotationService.delete(id);
        loadQuotations();
        Swal.fire('Deleted!', '', 'success');
      } catch (e) {
        console.error(e);
        Swal.fire({
          icon: 'error',
          title: 'Cannot Delete Quotation',
          text: e.response?.data?.error || 'Failed to delete quotation.',
        });
      }
    }
  };

  const handleEditQuotation = (q) => {
    setSelectedQuotation(q);
    setFormData({
      title: q.title || '',
      client: q.organization || '',
      organization: q.organization || '',
      tenderId: q.TenderId || '',
      toContact: q.toContact || '',
      designation: q.designation || '',
      subject: q.subject || '',
      submissionDate: q.submissionDate || '',
      validityDate: q.validityDate || '',
      validityDays: q.validityDate && q.submissionDate
        ? Math.ceil((new Date(q.validityDate) - new Date(q.submissionDate)) / (1000 * 60 * 60 * 24))
        : 30,
      gstRate: q.gstRate || 18.00,
      installationCharges: q.installationCharges || 0,
      status: q.status || 'Draft',
      currentStage: q.currentStage || 1,
      stageName: q.stageName || 'Draft',
      distributorLogo: q.distributorLogo || 'default',
      termsAndConditions: q.termsAndConditions || initialFormState.termsAndConditions
    });
    // Ensure existing items have an ID or tempId for keying
    setItems((q.QuotationItems || []).map(i => ({ ...i, _tempId: i.id || Date.now() + Math.random() })));
    setShowModal(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (e.target.tagName === 'TEXTAREA') return;
      // Allow Enter on Buttons (to trigger click)
      if (e.target.tagName === 'BUTTON') return;
      e.preventDefault();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { subTotal, gstAmount, grandTotal } = calculateTotals();

    const payload = {
      ...formData,
      title: formData.title || formData.subject || `Quotation for ${formData.client}`, // Ensure Title is present
      organization: formData.client, // Map client to organization
      TenderId: formData.tenderId || null,
      validityDate: calculateValidityDate(formData.submissionDate, formData.validityDays),
      gstRate: parseFloat(formData.gstRate) || 0,
      installationCharges: parseFloat(formData.installationCharges) || 0,
      subTotal,
      gstAmount,
      grandTotal,
      value: grandTotal,
      modifiedBy: user?.name || user?.username || 'System',
      quoteNo: selectedQuotation ? undefined : `QT-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}-${String(Math.floor(Math.random() * 100))}`,
      items: items.map(item => ({
        ...item,
        description: item.description || '',
        quantity: parseFloat(item.quantity) || 0,
        unitPrice: parseFloat(item.unitPrice) || 0,
        totalPrice: parseFloat(item.totalPrice) || 0
      }))
    };

    try {
      if (selectedQuotation) {
        await quotationService.update(selectedQuotation.id, payload);
        Swal.fire('Success', 'Updated!', 'success');
      } else {
        await quotationService.create(payload);
        Swal.fire('Success', 'Created!', 'success');
      }
      setShowModal(false);
      loadQuotations();

      // Refresh products list so any newly auto-added products are immediately available
      try {
        const prods = await productService.getAll();
        setProducts(prods.data || []);
      } catch (err) {
        console.error('Error refreshing products after quotation save:', err);
      }

    } catch (error) {
      console.error(error);
      setShowModal(false); // Close modal on error
      const errorMessage = error.response?.data?.error || 'Failed to save';
      Swal.fire('Error', errorMessage, 'error');
    }
  };

  const handleExportExcel = async (quotation) => {
    try {
      const companyRes = await companyService.getProfile();
      const company = companyRes.data || {};
      const user = JSON.parse(localStorage.getItem('rsms_user') || '{}');
      const userName = user.name || user.username || 'Authorized Signatory';

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Quotation');

      // --- Setup Columns Manually (Prevents Row 1 conflict) ---
      worksheet.getColumn(1).width = 6;
      worksheet.getColumn(2).width = 25;
      worksheet.getColumn(3).width = 20;
      worksheet.getColumn(4).width = 45;
      worksheet.getColumn(5).width = 10;
      worksheet.getColumn(6).width = 15;
      worksheet.getColumn(7).width = 15;

      // Page Setup for A4
      worksheet.pageSetup = { paperSize: 9, orientation: 'portrait' };

      let currentRow = 1;
      /*
      // --- 1. Logo & Header ---
      if (company.logo && company.logo.startsWith('data:image')) {
          // Logo Disabled for debugging
      }
      */

      // Company Name
      worksheet.mergeCells(`A${currentRow}:G${currentRow}`);
      const nameRow = worksheet.getRow(currentRow);
      nameRow.getCell(1).value = company.name || 'Company Name';
      nameRow.getCell(1).font = { bold: true, size: 16 };
      nameRow.getCell(1).alignment = { horizontal: 'center' };
      currentRow++;

      // Address
      worksheet.mergeCells(`A${currentRow}:G${currentRow}`);
      const addrRow = worksheet.getRow(currentRow);
      addrRow.getCell(1).value = company.address || '';
      addrRow.getCell(1).font = { size: 10 };
      addrRow.getCell(1).alignment = { horizontal: 'center' };
      currentRow++;

      // Contact
      worksheet.mergeCells(`A${currentRow}:G${currentRow}`);
      const contactRow = worksheet.getRow(currentRow);
      contactRow.getCell(1).value = `Tel: ${company.phone || ''} | Email: ${company.email || ''}`;
      contactRow.getCell(1).font = { size: 10 };
      contactRow.getCell(1).alignment = { horizontal: 'center' };
      currentRow += 2;

      // Divider
      worksheet.mergeCells(`A${currentRow}:G${currentRow}`);
      worksheet.getCell(`A${currentRow}`).border = { bottom: { style: 'thin' } };
      currentRow += 2;

      // --- 2. Quote Meta ---
      const startMetaRow = currentRow;

      // Right Side Labels
      worksheet.getCell(`F${currentRow}`).value = 'Quotation No:';
      worksheet.getCell(`F${currentRow}`).font = { bold: true };
      worksheet.getCell(`G${currentRow}`).value = quotation.quoteNo || '-';
      worksheet.getCell(`G${currentRow}`).alignment = { horizontal: 'right' };

      // Left Side To
      worksheet.getCell(`A${currentRow}`).value = 'To:';
      worksheet.getCell(`A${currentRow}`).font = { bold: true };
      currentRow++;

      worksheet.getCell(`F${currentRow}`).value = 'Date:';
      worksheet.getCell(`F${currentRow}`).font = { bold: true };
      worksheet.getCell(`G${currentRow}`).value = quotation.submissionDate || '-';
      worksheet.getCell(`G${currentRow}`).alignment = { horizontal: 'right' };

      worksheet.getCell(`A${currentRow}`).value = quotation.toContact || 'Contact Person';
      worksheet.getCell(`A${currentRow}`).font = { bold: true };
      currentRow++;

      worksheet.getCell(`F${currentRow}`).value = 'Valid Until:';
      worksheet.getCell(`F${currentRow}`).font = { bold: true };
      worksheet.getCell(`G${currentRow}`).value = quotation.validityDate || '-';
      worksheet.getCell(`G${currentRow}`).alignment = { horizontal: 'right' };

      worksheet.getCell(`A${currentRow}`).value = quotation.designation || '';
      currentRow++;

      worksheet.getCell(`F${currentRow}`).value = 'Status:';
      worksheet.getCell(`F${currentRow}`).font = { bold: true };
      worksheet.getCell(`G${currentRow}`).value = quotation.stageName || '-';
      worksheet.getCell(`G${currentRow}`).alignment = { horizontal: 'right' };

      worksheet.getCell(`A${currentRow}`).value = quotation.organization || 'Client Organization';
      currentRow += 2;

      // Subject
      worksheet.mergeCells(`A${currentRow}:G${currentRow}`);
      worksheet.getCell(`A${currentRow}`).value = `Subject: ${quotation.subject || 'Quotation'}`;
      worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 11, underline: true };
      currentRow += 2;

      // --- 3. Table ---
      const headerValues = ["S.No", "Product", "Model", "Description", "Qty", "Unit Price", "Total"];
      const headerRow = worksheet.getRow(currentRow);
      headerValues.forEach((val, idx) => {
        const cell = headerRow.getCell(idx + 1);
        cell.value = val;
        // Clean Style: No fills, just bold
        cell.font = { bold: true };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });
      currentRow++;

      (quotation.QuotationItems || []).forEach((item, index) => {
        const row = worksheet.getRow(currentRow);
        row.getCell(1).value = index + 1;
        row.getCell(2).value = item.productName || item.description || '-';
        row.getCell(3).value = item.modelNumber || '-';
        row.getCell(4).value = item.description || '-';
        row.getCell(5).value = item.quantity || 0;

        // Use adjusted unit price (Total / Qty) if possible, else fallback to unitPrice
        const qty = parseFloat(item.quantity) || 0;
        const total = parseFloat(item.totalPrice) || 0;
        const adjustedUnitPrice = (qty > 0) ? (total / qty) : parseFloat(item.unitPrice || 0);

        row.getCell(6).value = adjustedUnitPrice;
        row.getCell(7).value = total;

        [1, 5].forEach(c => row.getCell(c).alignment = { horizontal: 'center' });
        [6, 7].forEach(c => {
          row.getCell(c).alignment = { horizontal: 'right' };
          row.getCell(c).numFmt = '#,##0.00';
        });

        for (let i = 1; i <= 7; i++) {
          row.getCell(i).border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        }
        currentRow++;
      });

      // --- 4. Totals ---
      const addTotalRow = (label, value, isGrand = false) => {
        // Safe approach: Don't merge, just place in Col E and G
        const labelCell = worksheet.getCell(`E${currentRow}`);
        labelCell.value = label;
        labelCell.alignment = { horizontal: 'right' };
        labelCell.font = { bold: true };

        const valCell = worksheet.getCell(`G${currentRow}`);
        valCell.value = parseFloat(value || 0);
        valCell.numFmt = 'Rs. #,##0.00';
        valCell.alignment = { horizontal: 'right' };
        valCell.font = { bold: isGrand };

        // Removed Yellow Fills to prevent XML corruption
        currentRow++;
      };

      addTotalRow('Sub Total:', quotation.subTotal);
      if (quotation.gstAmount > 0) addTotalRow(`GST (${quotation.gstRate}%)`, quotation.gstAmount);
      if (quotation.installationCharges > 0) addTotalRow('Installation:', quotation.installationCharges);
      addTotalRow('Grand Total:', quotation.grandTotal, true);
      currentRow += 2;

      // --- 5. Terms ---
      worksheet.getCell(`A${currentRow}`).value = 'TERMS & CONDITIONS';
      worksheet.getCell(`A${currentRow}`).font = { bold: true, underline: true };
      currentRow++;

      const terms = [
        "1. Warranty: 1-Year",
        "2. Delivery: Within 1-3 weeks upon Confirmation of order",
        "3. Payment: 100% Advance - Negotiable",
        "4. Validity: 30 days",
        "5. Since there is rapid updation in technology, the quoted item/s are based on current stock with OEM.",
        "6. Taxes: All TAXES are NOT deductible from our bill.",
        "7. World Wide Vision will be not responsible for any failure to perform its obligations if prevented by force majeure.",
        "8. The scope of delivery of World Wide Vision is limited to above quoted items only.",
        "9. This quotation & its terms & conditions will be the part of contract."
      ];

      terms.forEach(term => {
        // Avoid merging in loop to prevent overlap risk
        worksheet.getCell(`A${currentRow}`).value = term;
        currentRow++;
      });
      currentRow += 3;

      // --- 6. Signature ---
      worksheet.getCell(`A${currentRow}`).value = 'Sincerely yours,';
      currentRow += 4;
      worksheet.getCell(`A${currentRow}`).value = userName;
      worksheet.getCell(`A${currentRow}`).font = { bold: true };
      currentRow++;
      worksheet.getCell(`A${currentRow}`).value = `For ${company.name || 'Company Name'}`;

      const signRow = currentRow - 1;
      // Simplified signature label
      worksheet.getCell(`E${signRow}`).value = 'Agreed by (Sign & Stamp)';
      worksheet.getCell(`E${signRow}`).alignment = { horizontal: 'center', vertical: 'top' };
      worksheet.getCell(`E${signRow}`).border = { top: { style: 'thin' } };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `${quotation.quoteNo || 'quotation'}.xlsx`);

    } catch (error) {
      console.error("Excel Export Error:", error);
      Swal.fire('Error', 'Failed to export Excel: ' + error.message, 'error');
    }
  };

  // PDF Generation
  const generatePDF = async (quotation, type = 'standard') => {
    try {
      const companyRes = await companyService.getProfile();
      const company = companyRes.data || {};

      // Explicit A4 Format
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      doc.setCharSpace(0);
      const pageWidth = doc.internal.pageSize.width;

      // --- Header Section ---
      let yPos = 15;

      // Logo (Left aligned)
      if (company.logo && company.logo.startsWith('data:image')) {
        try {
          // Dynamic format detection (defaults to PNG)
          const format = company.logo.includes('image/jpeg') ? 'JPEG' : 'PNG';
          doc.addImage(company.logo, format, 15, yPos, 40, 20); // Adjust size as needed
        } catch (err) { console.error("Logo error:", err); }
      }

      // Distributor Logos (Right aligned)
      let activeLogos = [];
      try {
        // Determine which logs to show based on quotation preference
        const pref = quotation.distributorLogo || 'default';
        let potentialLogos = [];

        if (company.distributorLogos) {
          const parsed = JSON.parse(company.distributorLogos);
          if (Array.isArray(parsed)) {
            potentialLogos = parsed;
          }
        }
        // Fallback legacy
        if (potentialLogos.length === 0 && company.distributorLogo) {
          potentialLogos.push({ src: company.distributorLogo, active: true });
        }


        if (pref === 'none') {
          activeLogos = [];
        } else if (pref === 'default' || !pref) {
          activeLogos = potentialLogos.filter(l => l.active);
        } else if (pref.startsWith('logo_')) {
          const index = parseInt(pref.replace('logo_', ''));
          if (potentialLogos[index]) {
            activeLogos = [potentialLogos[index]];
          }
        }
      } catch (e) {
        console.error("Error parsing distributorLogos", e);
      }

      if (activeLogos.length > 0) {
        const logoWidth = 35;
        const logoHeight = 15;
        const spacing = 2;
        const rightMargin = pageWidth - 15;

        activeLogos.reverse().forEach((logo, index) => {
          if (logo.src && logo.src.startsWith('data:image')) {
            try {
              const format = logo.src.includes('image/jpeg') ? 'JPEG' : 'PNG';
              const xPos = rightMargin - ((index + 1) * logoWidth) - (index * spacing);
              doc.addImage(logo.src, format, xPos, yPos, logoWidth, logoHeight);
            } catch (err) { console.error("Logo render error", err); }
          }
        });
      }

      // Company Details (Centered Title, Details below)

      // Company Name (Larger & Bold)
      doc.setFontSize(18); // Increased size
      doc.setFont(undefined, 'bold');
      doc.setTextColor(44, 62, 80); // Dark Blue/Grey
      doc.text(company.name || 'Company Name', pageWidth / 2, yPos + 8, { align: 'center' });

      // Address (Centered below name)
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100);

      const contactInfo = company.address || '';
      // City is usually part of address, but if separate:
      // if (company.city) contactInfo += `, ${company.city}`;  <-- redundant if address is full

      const contactLine = [
        company.phone ? `Tel: ${company.phone}` : '',
        company.email ? `Email: ${company.email}` : '',
        company.website ? `Web: ${company.website}` : ''
      ].filter(Boolean).join(' | ');

      let textY = yPos + 14;
      doc.text(contactInfo, pageWidth / 2, textY, { align: 'center' });
      textY += 5;
      doc.text(contactLine, pageWidth / 2, textY, { align: 'center' });
      textY += 5;

      // Divider Line
      doc.setDrawColor(200);
      doc.line(15, textY + 5, pageWidth - 15, textY + 5);

      // --- Quotation Info Section ---
      yPos = textY + 15;

      // Left Side: Customer Details
      doc.setFontSize(11);
      doc.setTextColor(44, 62, 80);
      doc.setFont(undefined, 'bold');
      doc.text('To:', 15, yPos);

      doc.setFontSize(10);
      doc.setTextColor(0);
      doc.setFont(undefined, 'bold');
      doc.text(quotation.toContact || 'Contact Person', 15, yPos + 6);
      doc.setFont(undefined, 'normal');
      doc.text(quotation.designation || '', 15, yPos + 11);
      doc.text(quotation.organization || 'Client Organization', 15, yPos + 16);

      // Right Side: Quotation Meta
      const rightX = pageWidth - 60;
      doc.setFont(undefined, 'bold');
      doc.text(`Quotation No:`, rightX, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(quotation.quoteNo || '-', rightX + 25, yPos, { align: 'left' });

      doc.setFont(undefined, 'bold');
      doc.text(`Date:`, rightX, yPos + 6);
      doc.setFont(undefined, 'normal');
      doc.text(quotation.submissionDate || '-', rightX + 25, yPos + 6, { align: 'left' });

      if (quotation.validityDate) {
        doc.setFont(undefined, 'bold');
        doc.text(`Valid Until:`, rightX, yPos + 12);
        doc.setFont(undefined, 'normal');
        doc.text(quotation.validityDate, rightX + 25, yPos + 12, { align: 'left' });
      }

      // Subject
      doc.setFont(undefined, 'bold');
      doc.setFontSize(11);
      doc.text(`Subject: ${quotation.subject || 'Quotation'}`, 15, yPos + 30);

      // --- Table Section ---
      let tableColumn = ["S.No", "Product", "Model", "Description", "Qty", "Unit Price", "Total"];
      // If technical, remove Price, Adjustment and Total
      if (type === 'technical') {
        tableColumn = ["S.No", "Product", "Model", "Description", "Qty"];
      }

      const tableRows = [];
      const qItems = quotation.QuotationItems || [];

      qItems.forEach((item, index) => {
        // Flatten text: remove manual line breaks and collapse multiple spaces to single space, also normalize special dashes
        const rawDesc = (item.description || '').replace(/[\u2011\u2012\u2013\u2014\u2015]/g, '-');
        const cleanDescription = rawDesc.replace(/\r?\n/g, ' ').replace(/\s+/g, ' ').trim();
        // Calculate adjusted unit price (Total / Qty) for display
        const qty = parseFloat(item.quantity) || 0;
        const total = parseFloat(item.totalPrice) || 0;
        const adjustedUnitPrice = (qty > 0) ? (total / qty) : parseFloat(item.unitPrice || 0);

        let row = [
          index + 1,
          (item.productName || '').replace(/[\u2011\u2012\u2013\u2014\u2015]/g, '-') || cleanDescription || '-',
          (item.modelNumber || '').replace(/[\u2011\u2012\u2013\u2014\u2015]/g, '-') || '-',
          cleanDescription || '-',
          item.quantity || 0,
          adjustedUnitPrice.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
          total.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        ];

        if (type === 'technical') {
          // Slice to exclude the last two columns (Unit Price, Total)
          // S.No, Product, Model, Description, Qty (0,1,2,3,4) - 5 items
          row = row.slice(0, 5);
        }
        tableRows.push(row);
      });

      // Add Totals Rows ONLY if NOT technical
      if (type !== 'technical') {
        const colSpanCount = 6;
        // We span 6 columns for the label, and put the value in the 7th column
        tableRows.push([
          { content: 'Sub Total', colSpan: colSpanCount, styles: { halign: 'right', fontStyle: 'bold' } },
          { content: `Rs. ${parseFloat(quotation.subTotal || 0).toLocaleString()}`, styles: { halign: 'right' } }
        ]);

        if (quotation.gstAmount > 0) {
          tableRows.push([
            { content: `GST (${quotation.gstRate || 0}%)`, colSpan: colSpanCount, styles: { halign: 'right', fontStyle: 'bold' } },
            { content: `Rs. ${parseFloat(quotation.gstAmount || 0).toLocaleString()}`, styles: { halign: 'right' } }
          ]);
        }

        if (quotation.installationCharges > 0) {
          tableRows.push([
            { content: 'Installation/Comm:', colSpan: colSpanCount, styles: { halign: 'right', fontStyle: 'bold' } },
            { content: `Rs. ${parseFloat(quotation.installationCharges || 0).toLocaleString()}`, styles: { halign: 'right' } }
          ]);
        }

        // Grand Total
        tableRows.push([
          { content: 'Grand Total', colSpan: colSpanCount, styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 255, 0] } }, // Yellow Background
          { content: `Rs. ${parseFloat(quotation.grandTotal || 0).toLocaleString()}`, styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 255, 0] } }
        ]);
      }

      // Reset character spacing explicitly before table
      doc.setCharSpace(0);

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: yPos + 35,
        theme: 'grid', // Keeps borders
        headStyles: {
          fillColor: [44, 62, 80], // Dark header
          textColor: 255,
          fontSize: 10,
          halign: 'center'
        },
        styles: {
          fontSize: 9,
          cellPadding: 3,
          valign: 'middle',
          halign: 'left', // Global default to Left (removes justify)
          overflow: 'linebreak' // Ensure natural wrapping
        },
        columnStyles: type === 'technical' ? {
          0: { halign: 'center', cellWidth: 15 }, // S.No
          3: { halign: 'left' }, // Description
          4: { halign: 'center' }  // Qty
        } : {
          0: { halign: 'center', cellWidth: 15 }, // S.No
          3: { halign: 'left' }, // Description
          4: { halign: 'center' }, // Qty
          5: { halign: 'right' }, // Unit Price
          6: { halign: 'right' }  // Total
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        }
      });

      // --- Footer Link ---
      let finalY = doc.lastAutoTable.finalY + 10;
      const totalX = pageWidth - 15;

      // Removed manual addTotalLine calls as they are now in the table

      // --- Footer ---
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text('Thank you for your business.', pageWidth / 2, doc.internal.pageSize.height - 10, { align: 'center' });
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - 15, doc.internal.pageSize.height - 10, { align: 'right' });
      }

      // --- Terms & Conditions Page ---
      doc.addPage();
      doc.setFont(undefined, 'bold');
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text('TERMS & CONDITIONS', 15, 20);

      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);

      console.log('Generating PDF for:', quotation);
      console.log('Terms found:', quotation.termsAndConditions);

      const terms = (quotation.termsAndConditions || `1. Warranty: 1-Year
2. Delivery: Within 1-3 weeks upon Confirmation of order
3. Payment: 100% Advance - Negotiable
4. Validity: 30 days
5. Since there is rapid updation in technology, the quoted item/s are based on current stock with OEM. In case the offered item is discontinued or Not available at the time of PO, alternate / equivalent item will be supplied.
6. Taxes: All TAXES are NOT deductible from our bill. All items will be imported through proper channel. All applicable taxes will be paid at the time of import, Since FBR has prohibited deduction of double tax at the time of payment. Therefore deductioin of any tax from our bill will not be accepetable. Incase tax is deducted from our bill, same amount will be invoiced to end customer for payment.
7. World Wide Vision will be not responsible for any failure to perform its obligations under this Quotation, if it is prevented or delayed in performing those obligations by an event of force majeure.
8. The scope of delivery of World Wide Vision is limited to above quoted items only. Any additional item/material will be subject to written prior approval of additional price approval separately.
9. This quotation & its terms & conditions will be the part of contract.`).split('\n');

      let termY = 30;
      doc.setFontSize(9);
      terms.forEach(term => {
        // Simple heuristic to detect if line starts with number
        let safeTerm = term;
        // if (!/^\d/.test(safeTerm.trim())) { safeTerm = "• " + safeTerm; } 

        const splitText = doc.splitTextToSize(safeTerm, pageWidth - 30);
        doc.text(splitText, 15, termY);
        termY += (splitText.length * 4) + 2;
      });

      termY += 10;
      doc.text("Looking forward for your esteemed purchase order.", 15, termY);

      termY += 15;
      doc.text("Sincerely yours", 15, termY);

      // Dynamic Signatures
      // Fetch fresh user data from API to get signature
      let userSignature = '';
      try {
        const storedUser = JSON.parse(localStorage.getItem('rsms_user') || '{}');
        if (storedUser.id) {
          console.log("Fetching signature for user ID:", storedUser.id);
          const uRes = await authService.getUser(storedUser.id);
          if (uRes.data && uRes.data.signature) {
            userSignature = uRes.data.signature;
            console.log("Signature found, length:", userSignature.length);
          } else {
            console.log("No signature found in fresh user data.");
          }
        }
      } catch (e) { console.error('Error fetching user signature for PDF', e); }

      const user = JSON.parse(localStorage.getItem('rsms_user') || '{}');
      const userName = user.name || user.username || 'Authorized Signatory';

      const lineStart = pageWidth - 80;
      const lineEnd = pageWidth - 15;
      const lineCenter = (lineStart + lineEnd) / 2;

      termY += 50; // Space for signatures

      // Left Side: Sincerely Yours
      // Place User Signature ABOVE the name
      if (userSignature && userSignature.startsWith('data:image')) {
        try {
          const sigFormat = userSignature.includes('image/jpeg') ? 'JPEG' : 'PNG';
          // Align with left text (x=15)
          // termY is where the text starts, so image goes above it
          doc.addImage(userSignature, sigFormat, 15, termY - 25, 40, 20);
        } catch (e) { console.warn('User Sig Error', e); }
      }

      doc.setFont(undefined, 'bold');
      doc.text(userName, 15, termY);
      doc.setFont(undefined, 'normal');
      doc.text(`For ${company.name || 'Company Name'}`, 15, termY + 5);

      // Right Side: Agreed by (Sign & Stamp)

      // Place Company Stamp ABOVE 'Agreed by' line
      if (company.stamp && company.stamp.startsWith('data:image')) {
        try {
          const stampFormat = company.stamp.includes('image/jpeg') ? 'JPEG' : 'PNG';
          // Center on the line
          doc.addImage(company.stamp, stampFormat, lineCenter - 15, termY - 35, 30, 30);
        } catch (e) { console.warn('Stamp Error', e); }
      }

      doc.line(lineStart, termY, lineEnd, termY);
      doc.setFontSize(8);
      doc.text("Agreed by", lineCenter, termY + 4, { align: 'center' });
      doc.text("(Sign & Stamp)", lineCenter, termY + 8, { align: 'center' });

      // Determine Filename
      const filenamePrefix = type === 'technical' ? 'Technical Quotation' : 'Quotation';
      doc.save(`${filenamePrefix} - ${quotation.quoteNo || 'draft'}.pdf`);
    } catch (error) {
      console.error("PDF Generation Error:", error);
      Swal.fire('Error', 'Failed to generate PDF. Check console for details.', 'error');
    }
  };

  const getStageBadge = (id) => {
    const stage = stages.find(s => s.id === id);
    return `badge bg-${stage ? stage.color : 'secondary'}`;
  };

  const handleStatusClick = (q) => {
    setSelectedQuotation(q);
    setFormData({
      ...formData,
      currentStage: q.currentStage,
      stageName: q.stageName
    });
    setShowStageModal(true);
  };

  const handleStageSubmit = async (e) => {
    e.preventDefault();
    try {
      const stage = stages.find(s => s.id === parseInt(formData.currentStage));
      if (!stage) return;

      const payload = {
        currentStage: stage.id,
        stageName: stage.name,
        status: (stage.name === 'Awarded') ? 'Awarded' :
          (stage.name === 'Rejected' || stage.name === 'Cancelled') ? 'Cancelled' : // Map Rejected to Cancelled or keep generic? The ENUM has 'Rejected' (no wait, ENUM has 'Cancelled', 'Lost', etc. Let's use stage.name if it matches ENUM)
            stage.name // Default to using the Stage Name as Status if they align (e.g. 'Draft', 'Submitted')
      };

      await quotationService.updateStage(selectedQuotation.id, payload);
      setShowStageModal(false);
      loadQuotations();
      Swal.fire('Success', `Status updated to ${stage.name}`, 'success');
    } catch (error) {
      console.error(error);
      setShowStageModal(false); // Close modal so alert is visible
      Swal.fire('Error', 'Failed to update status', 'error');
    }
  };

  const { subTotal, gstAmount, grandTotal } = calculateTotals();

  const filteredQuotations = quotations.filter(q =>
    !searchTerm ||
    (q.quoteNo && q.quoteNo.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (q.title && q.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (q.organization && q.organization.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (q.toContact && q.toContact.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (q.subject && q.subject.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2 className="mb-1" style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: '700',
            fontSize: '2rem'
          }}>
            Quotation Management
          </h2>
          <p className="text-muted mb-0">Create, track and manage client quotations</p>
        </div>

        {canManage && (
          <button className="btn btn-primary" onClick={() => {
            setSelectedQuotation(null);
            setFormData(initialFormState);
            setItems([]);
            setShowModal(true);
          }}>
            <i className="bi bi-plus-lg me-1"></i> Create Quotation
          </button>
        )}
      </div>

      <div className="mb-3">
        <div className="input-group">
          <span className="input-group-text bg-white"><i className="bi bi-search"></i></span>
          <input
            type="text"
            className="form-control"
            placeholder="Search quotations by Ref No, Organization, Contact, Or Subject..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Ref No</th>
                  <th>Target Tender</th>
                  <th>Organization</th>
                  <th>Subject</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Designation</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan="8" className="text-center">Loading...</td></tr> : filteredQuotations.map(q => (
                  <tr key={q.id}>
                    <td><strong>{q.quoteNo}</strong></td>
                    <td>
                      {q.Tender ? (
                        <span className="badge bg-light text-dark border" title={q.Tender.title}>
                          <i className="bi bi-link-45deg me-1"></i>{q.Tender.tenderNo}
                        </span>
                      ) : <span className="text-muted">-</span>}
                    </td>
                    <td>{q.organization}</td>
                    <td>{q.subject}</td>
                    <td>{q.submissionDate}</td>
                    <td>Rs. {parseFloat(q.grandTotal).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                    <td><span className={getStageBadge(q.currentStage)}>{q.stageName}</span></td>
                    <td>{q.designation || '-'}</td>
                    <td>

                      {canManage && (
                        <>
                          <button className="btn btn-sm btn-outline-info me-1" onClick={() => { setSelectedQuotation(q); setShowViewModal(true); }} title="View Details"><i className="bi bi-eye"></i></button>
                          <button className="btn btn-sm btn-outline-warning me-1" onClick={() => handleStatusClick(q)} title="Update Status"><i className="bi bi-arrow-repeat"></i></button>
                          <button
                            className={`btn btn-sm btn-outline-primary me-1 ${!(q.currentStage === 1 || q.currentStage === 6) ? 'disabled' : ''}`}
                            onClick={() => (q.currentStage === 1 || q.currentStage === 6) && handleEditQuotation(q)}
                            title={!(q.currentStage === 1 || q.currentStage === 6) ? "Cannot edit after submission" : "Edit"}
                            disabled={!(q.currentStage === 1 || q.currentStage === 6)}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-danger me-1" onClick={() => handleDeleteQuotation(q.id)} title="Delete"><i className="bi bi-trash"></i></button>
                        </>
                      )}
                      <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => generatePDF(q, 'technical')} title="Download Technical Quotation"><i className="bi bi-file-earmark-code"></i></button>
                      <button className="btn btn-sm btn-outline-dark me-1" onClick={() => generatePDF(q, 'standard')} title="Download Financial Quotation"><i className="bi bi-file-pdf"></i></button>
                    </td>
                  </tr>
                ))}
                {!loading && filteredQuotations.length === 0 && <tr><td colSpan="8" className="text-center">No quotations found</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Stage Update Modal */}
      {
        showStageModal && createPortal(
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
            <div className="modal-dialog modal-sm modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Update Status</h5>
                  <button type="button" className="btn-close" onClick={() => setShowStageModal(false)}><i className="bi bi-x-lg"></i></button>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleStageSubmit}>
                    <div className="mb-3">
                      <label className="form-label">Select Stage</label>
                      <select
                        className="form-select"
                        value={formData.currentStage}
                        onChange={(e) => setFormData({ ...formData, currentStage: e.target.value })}
                      >
                        {stages.filter(s => {
                          if (!selectedQuotation) return true;
                          // If current is Draft (1), only allow Draft (1) or Submitted (2)
                          if (selectedQuotation.currentStage === 1) {
                            return s.id === 1 || s.id === 2;
                          }
                          // If current is Submitted (2) or greater, do NOT allow Draft (1)
                          if (selectedQuotation.currentStage >= 2 && s.id === 1) {
                            return false;
                          }
                          // If current is Submitted (2), allow Revision (6) or Awarded/Rejected. 
                          // If current is Revision (6), allow Submitted (2).
                          // Logic automatically handles this as long as we don't block them.

                          return true;
                        }).map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="d-flex justify-content-end">
                      <button type="button" className="btn btn-secondary me-2" onClick={() => setShowStageModal(false)}>Cancel</button>
                      <button type="submit" className="btn btn-primary">Update Status</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>, document.body
        )
      }

      {/* Create/Edit Modal */}
      {
        showModal && createPortal(
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">{selectedQuotation ? 'Edit Quotation' : 'New Quotation'}</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}><i className="bi bi-x-lg"></i></button>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
                    {/* Header Info */}
                    <div className="row g-3 mb-3">
                      <div className="col-12">
                        <div className="card bg-light border-0">
                          <div className="card-body py-2">
                            <div className="row align-items-center">
                              <label className="col-sm-3 col-form-label fw-bold">Link to Tender (Optional):</label>
                              <div className="col-sm-9">
                                <select
                                  className="form-select form-select-sm"
                                  value={formData.tenderId || ''}
                                  onChange={(e) => {
                                    const tId = e.target.value;
                                    const selectedTender = tenders.find(t => t.id === parseInt(tId));
                                    let updates = { tenderId: tId };

                                    if (selectedTender && selectedTender.organization) {
                                      const orgName = selectedTender.organization;
                                      updates.client = orgName;
                                      updates.organization = orgName;

                                      // Auto-Find Contact based on Organization
                                      const org = organizations.find(o => o.name === orgName);
                                      let contact = null;
                                      if (org) {
                                        contact = contacts.find(c => c.OrganizationId === org.id);
                                        if (!contact && org.contactPerson) {
                                          contact = contacts.find(c => c.name === org.contactPerson);
                                        }
                                      }

                                      if (contact) {
                                        updates.toContact = contact.name;
                                        updates.designation = contact.designation || '';
                                      } else if (org && org.contactPerson) {
                                        updates.toContact = org.contactPerson;
                                        updates.designation = ''; // Reset or keep previous? Better reset to avoid mismatch
                                      }
                                    }
                                    setFormData({ ...formData, ...updates });
                                  }}
                                >
                                  <option value="">-- No Tender Linked --</option>
                                  {tenders.filter(t => t.status === 'active').map(t => (
                                    <option key={t.id} value={t.id}>
                                      {t.tenderNo} - {t.title.substring(0, 50)}...
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label">Client (Organization) *</label>
                        <select className="form-select" required value={formData.client} onChange={(e) => {
                          const selectedName = e.target.value;
                          const org = organizations.find(o => o.name === selectedName);
                          let contact = null;
                          if (org) {
                            // First try to find by OrganizationId if available in contacts
                            contact = contacts.find(c => c.OrganizationId === org.id);
                            // If not found, try to match by name if org has contactPerson
                            if (!contact && org.contactPerson) {
                              contact = contacts.find(c => c.name === org.contactPerson);
                            }
                          }
                          setFormData({
                            ...formData,
                            client: selectedName,
                            toContact: contact ? contact.name : (org?.contactPerson || ''),
                            designation: contact ? contact.designation : ''
                          });
                        }}>
                          <option value="">Select Client</option>
                          {organizations.map(o => <option key={o.id} value={o.name}>{o.name}</option>)}
                        </select>
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label">To (Contact Person)</label>
                        <select
                          className="form-select"
                          value={formData.toContact}
                          onChange={e => {
                            const selectedContactName = e.target.value;
                            const contact = contacts.find(c => c.name === selectedContactName && (c.OrganizationId === organizations.find(o => o.name === formData.client)?.id || !c.OrganizationId));
                            setFormData({
                              ...formData,
                              toContact: selectedContactName,
                              designation: contact ? contact.designation : ''
                            });
                          }}
                        >
                          <option value="">Select Contact</option>
                          {/* Filter contacts by selected organization */}
                          {(() => {
                            const selectedOrg = organizations.find(o => o.name === formData.client);
                            const orgContacts = contacts.filter(c => selectedOrg && c.OrganizationId === selectedOrg.id);

                            // Also include the Organization's main contact person if not already in the list
                            if (selectedOrg && selectedOrg.contactPerson) {
                              const exists = orgContacts.some(c => c.name === selectedOrg.contactPerson);
                              if (!exists) {
                                // Create a pseudo-contact option for the main contact person
                                orgContacts.unshift({ id: 'main', name: selectedOrg.contactPerson, designation: 'Main Contact' });
                              }
                            }

                            return orgContacts.map(c => (
                              <option key={c.id || c.name} value={c.name}>
                                {c.name} {c.designation ? `(${c.designation})` : ''}
                              </option>
                            ));
                          })()}
                        </select>
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label">Designation</label>
                        <input type="text" className="form-control" value={formData.designation} onChange={e => setFormData({ ...formData, designation: e.target.value })} />
                      </div>
                    </div>
                    <div className="row g-3 mb-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label">Submission Date *</label>
                        <input type="date" className="form-control" required value={formData.submissionDate} onChange={e => setFormData({ ...formData, submissionDate: e.target.value })} />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label">Validity (Days)</label>
                        <input
                          type="number"
                          className="form-control"
                          min="1"
                          value={formData.validityDays}
                          onChange={e => setFormData({ ...formData, validityDays: parseInt(e.target.value) || 0 })}
                        />
                        <small className="text-muted">
                          Valid until: {calculateValidityDate(formData.submissionDate, formData.validityDays)}
                        </small>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Subject</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          value={formData.subject}
                          onChange={e => setFormData({ ...formData, subject: e.target.value })}
                        />
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => setFormData({ ...formData, subject: cleanText(formData.subject) })}
                          title="Cleanup PDF artifacts"
                        >
                          <i className="bi bi-magic"></i>
                        </button>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Authorized Distributor Logo</label>
                      <select
                        className="form-select"
                        value={formData.distributorLogo || 'default'}
                        onChange={e => setFormData({ ...formData, distributorLogo: e.target.value })}
                      >
                        <option value="default">Default (Authorized Distributor)</option>
                        <option value="none">None (Hidden)</option>
                        {(() => {
                          try {
                            if (company.distributorLogos) {
                              return JSON.parse(company.distributorLogos).map((logo, idx) => (
                                <option key={idx} value={`logo_${idx}`}>
                                  {logo.name || `Authorized Distributor ${idx + 1}`}
                                </option>
                              ));
                            }
                            if (company.distributorLogo) {
                              return <option value="logo_0">Main Logo</option>;
                            }
                          } catch (e) { return null; }
                        })()}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Terms & Conditions</label>
                      <textarea
                        className="form-control"
                        rows="6"
                        value={formData.termsAndConditions || ''}
                        onChange={e => setFormData({ ...formData, termsAndConditions: e.target.value })}
                      ></textarea>
                      <div className="form-text">Terms are split by new line in the PDF.</div>
                    </div>

                    {/* Line Items */}
                    <h6 className="mt-4 border-bottom pb-2">Line Items</h6>
                    <div className="table-responsive mb-3">
                      <table className="table align-middle">
                        <thead className="table-light">
                          <tr>
                            <th style={{ minWidth: '200px' }}>Product</th>
                            <th style={{ minWidth: '150px' }}>Model No</th>
                            <th style={{ minWidth: '250px' }}>Description</th>
                            <th style={{ minWidth: '100px' }}>Qty</th>
                            <th style={{ minWidth: '150px' }}>Unit Price</th>
                            <th style={{ minWidth: '200px' }}>Adjustment</th>
                            <th style={{ minWidth: '150px' }}>Total Price</th>
                            <th style={{ width: '50px' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item, index) => (
                            <tr key={item._tempId || item.id || index}>
                              <td>
                                <div className="input-group input-group-sm">
                                  <input
                                    list={`products-name-${index}`}
                                    className="form-control"
                                    placeholder="Search Product..."
                                    value={item.productName || ''}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      const prod = products.find(p => p.name === val);
                                      if (prod) {
                                        handleItemChange(index, {
                                          productName: val,
                                          modelNumber: prod.modelNumber,
                                          description: prod.description || '',
                                          unitPrice: prod.price
                                        });
                                      } else {
                                        handleItemChange(index, 'productName', val);
                                      }
                                    }}
                                  />
                                  <button
                                    className="btn btn-outline-secondary"
                                    type="button"
                                    onClick={() => handleItemChange(index, 'productName', cleanText(item.productName))}
                                    title="Cleanup"
                                  >
                                    <i className="bi bi-magic"></i>
                                  </button>
                                </div>
                                <datalist id={`products-name-${index}`}>
                                  {products.map(p => <option key={`n-${p.id}`} value={p.name} />)}
                                </datalist>
                              </td>
                              <td>
                                <div className="input-group input-group-sm">
                                  <input
                                    list={`products-model-${index}`}
                                    className="form-control"
                                    placeholder="Search Model..."
                                    value={item.modelNumber}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      const prod = products.find(p => p.modelNumber === val);
                                      if (prod) {
                                        handleItemChange(index, {
                                          modelNumber: val,
                                          productName: prod.name,
                                          description: prod.description || '',
                                          unitPrice: prod.price
                                        });
                                      } else {
                                        handleItemChange(index, 'modelNumber', val);
                                      }
                                    }}
                                  />
                                  <button
                                    className="btn btn-outline-secondary"
                                    type="button"
                                    onClick={() => handleItemChange(index, 'modelNumber', cleanText(item.modelNumber))}
                                    title="Cleanup"
                                  >
                                    <i className="bi bi-magic"></i>
                                  </button>
                                </div>
                                <datalist id={`products-model-${index}`}>
                                  {products.map(p => <option key={`m-${p.id}`} value={p.modelNumber}>{p.name}</option>)}
                                </datalist>
                              </td>
                              <td>
                                <div className="input-group input-group-sm">
                                  <textarea
                                    className="form-control"
                                    rows="3"
                                    value={item.description || ''}
                                    onChange={e => handleItemChange(index, 'description', e.target.value)}
                                  />
                                  <button
                                    className="btn btn-outline-secondary"
                                    type="button"
                                    onClick={() => handleItemChange(index, 'description', cleanText(item.description))}
                                    title="Cleanup"
                                  >
                                    <i className="bi bi-magic"></i>
                                  </button>
                                </div>
                              </td>
                              <td><input type="number" className="form-control" min="1" value={item.quantity} onChange={e => handleItemChange(index, 'quantity', e.target.value)} /></td>
                              <td><input type="number" className="form-control" min="0" value={item.unitPrice} onChange={e => handleItemChange(index, 'unitPrice', e.target.value)} /></td>
                              <td>
                                <div className="d-flex gap-1">
                                  <select
                                    className="form-select form-select-sm"
                                    style={{ width: '65px' }}
                                    value={item.adjustmentType || 'None'}
                                    onChange={e => handleItemChange(index, 'adjustmentType', e.target.value)}
                                  >
                                    <option value="None">Off</option>
                                    <option value="Add">+</option>
                                    <option value="Subtract">-</option>
                                  </select>
                                  <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    placeholder="%"
                                    style={{ width: '80px' }}
                                    value={item.adjustmentPercentage || 0}
                                    onChange={e => handleItemChange(index, 'adjustmentPercentage', parseFloat(e.target.value) || 0)}
                                  />
                                </div>
                              </td>
                              <td>{parseFloat(item.totalPrice).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                              <td><button type="button" className="btn btn-outline-danger" onClick={() => handleRemoveItem(index)}><i className="bi bi-trash"></i></button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <button type="button" className="btn btn-sm btn-secondary mb-3" onClick={handleAddItem}>+ Add Item</button>

                    {/* Footer / Totals */}
                    <div className="row justify-content-end">
                      <div className="col-12 col-md-6 col-lg-4">
                        <div className="d-flex justify-content-between mb-2">
                          <span>Sub Total:</span>
                          <strong>{subTotal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</strong>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span>GST Rate (%):</span>
                          <input type="number" className="form-control form-control-sm w-25 text-end" value={formData.gstRate} onChange={e => setFormData({ ...formData, gstRate: e.target.value })} />
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span>GST Amount:</span>
                          <strong>{gstAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</strong>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span>Install/Commission:</span>
                          <input type="number" className="form-control form-control-sm w-50 text-end" value={formData.installationCharges} onChange={e => setFormData({ ...formData, installationCharges: e.target.value })} />
                        </div>
                        <div className="d-flex justify-content-between border-top pt-2 mt-2 bg-light p-2 rounded">
                          <h5 className="mb-0">Grand Total:</h5>
                          <h5 className="mb-0 text-primary">Rs. {grandTotal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</h5>
                        </div>
                      </div>
                    </div>

                    <div className="modal-footer px-0 pb-0 mt-3 border-top-0">
                      <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                      <button type="submit" className="btn btn-primary px-4">{selectedQuotation ? 'Update Quotation' : 'Create Quotation'}</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div >, document.body
        )
      }

      {/* View Quotation Modal */}
      {
        showViewModal && selectedQuotation && createPortal(
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1070 }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Quotation Preview - {selectedQuotation.quoteNo}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowViewModal(false)}><i className="bi bi-x-lg"></i></button>
                </div>
                <div className="modal-body p-4 bg-light">
                  <div className="card border-0 shadow-sm" id="quotation-preview">
                    <div className="card-body p-5">
                      {/* Header */}
                      <div className="d-flex justify-content-between align-items-center mb-5 border-bottom pb-4">
                        <div>
                          <h2 className="fw-bold text-primary mb-1">{company.name || 'Company Name'}</h2>
                          <div className="text-muted small">
                            {company.address}<br />
                            Phone: {company.phone} | Email: {company.email}
                          </div>
                        </div>
                        <div className="text-end">
                          <h4 className="fw-bold">QUOTATION</h4>
                          <span className={`badge ${getStageBadge(selectedQuotation.currentStage).replace('badge ', 'bg-')}`}>{selectedQuotation.stageName}</span>
                        </div>
                      </div>

                      {/* Info Grid */}
                      <div className="row mb-5">
                        <div className="col-md-6">
                          <h6 className="text-uppercase text-muted small fw-bold mb-3">Ref/Date</h6>
                          <table className="table table-sm table-borderless">
                            <tbody>
                              <tr>
                                <th className="ps-0" style={{ width: '100px' }}>Quote No:</th>
                                <td>{selectedQuotation.quoteNo}</td>
                              </tr>
                              <tr>
                                <th className="ps-0">Date:</th>
                                <td>{selectedQuotation.submissionDate}</td>
                              </tr>
                              <tr>
                                <th className="ps-0">Valid Until:</th>
                                <td>{selectedQuotation.validityDate}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="col-md-6">
                          <h6 className="text-uppercase text-muted small fw-bold mb-3">Customer</h6>
                          <div className="fw-bold">{selectedQuotation.toContact || 'Contact Person'}</div>
                          <div>{selectedQuotation.designation}</div>
                          <div>{selectedQuotation.organization}</div>
                        </div>
                      </div>

                      {/* Subject */}
                      <div className="mb-4">
                        <h6 className="text-uppercase text-muted small fw-bold">Subject</h6>
                        <p className="fw-bold fs-5">{selectedQuotation.subject}</p>
                      </div>

                      {/* Items Table */}
                      <div className="table-responsive mb-4">
                        <table className="table table-striped table-bordered">
                          <thead>
                            <tr style={{ backgroundColor: '#2c3e50', color: 'white' }}>
                              <th style={{ width: '5%', backgroundColor: '#2c3e50', color: 'white' }}>#</th>
                              <th style={{ width: '25%', backgroundColor: '#2c3e50', color: 'white' }}>Product</th>
                              <th style={{ width: '15%', backgroundColor: '#2c3e50', color: 'white' }}>Model</th>
                              <th style={{ width: '30%', backgroundColor: '#2c3e50', color: 'white' }}>Description</th>
                              <th style={{ width: '5%', backgroundColor: '#2c3e50', color: 'white' }} className="text-center">Qty</th>
                              <th style={{ width: '10%', backgroundColor: '#2c3e50', color: 'white' }} className="text-end">Unit Price</th>
                              <th style={{ width: '10%', backgroundColor: '#2c3e50', color: 'white' }} className="text-center">Adj</th>
                              <th style={{ width: '10%', backgroundColor: '#2c3e50', color: 'white' }} className="text-end">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(selectedQuotation.QuotationItems || []).map((item, idx) => (
                              <tr key={idx}>
                                <td>{idx + 1}</td>
                                <td className="fw-bold">{item.productName}</td>
                                <td>{item.modelNumber}</td>
                                <td style={{ whiteSpace: 'pre-line' }}>{item.description}</td>
                                <td className="text-center">{item.quantity}</td>
                                <td className="text-end">{parseFloat(item.unitPrice).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                                <td className="text-center">
                                  {item.adjustmentType !== 'None' ? `${item.adjustmentType === 'Add' ? '+' : '-'}${item.adjustmentPercentage}%` : '-'}
                                </td>
                                <td className="text-end">{parseFloat(item.totalPrice).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Totals */}
                      <div className="row justify-content-end mb-4">
                        <div className="col-md-5 col-lg-4">
                          <table className="table table-sm table-borderless">
                            <tbody>
                              <tr>
                                <td className="fw-bold text-end">Sub Total:</td>
                                <td className="text-end w-50">{parseFloat(selectedQuotation.subTotal).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                              </tr>
                              {selectedQuotation.gstAmount > 0 && (
                                <tr>
                                  <td className="fw-bold text-end">GST ({selectedQuotation.gstRate}%):</td>
                                  <td className="text-end">{parseFloat(selectedQuotation.gstAmount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                                </tr>
                              )}
                              {selectedQuotation.installationCharges > 0 && (
                                <tr>
                                  <td className="fw-bold text-end">Installation:</td>
                                  <td className="text-end">{parseFloat(selectedQuotation.installationCharges).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                                </tr>
                              )}
                              <tr className="border-top border-dark">
                                <td className="fw-bold text-end fs-5">Grand Total:</td>
                                <td className="fw-bold text-end fs-5 text-primary">Rs. {parseFloat(selectedQuotation.grandTotal).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Terms */}
                      {selectedQuotation.termsAndConditions && (
                        <div className="border-top pt-4">
                          <h6 className="text-uppercase text-muted small fw-bold mb-2">Terms & Conditions</h6>
                          <div className="small text-muted" style={{ whiteSpace: 'pre-line' }}>
                            {selectedQuotation.termsAndConditions}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer bg-light">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowViewModal(false)}>Close Preview</button>
                  <button type="button" className="btn btn-primary" onClick={() => generatePDF(selectedQuotation)}><i className="bi bi-file-pdf me-1"></i> Download PDF</button>
                </div>
              </div>
            </div>
          </div >, document.body
        )
      }
    </div >
  );
};

export default QuotationManagement;
